import { Loader } from '@10xLabs/react-web/components/Loader'
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client'
import { FieldArray, Formik, getIn } from 'formik'
import { isEmpty, isNull } from 'lodash'
import { Fragment, ReactElement, useRef, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Label } from 'components/v2/Label'
import { Input } from 'components/Input'
import { InputNumber } from 'components/InputNumber'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import IconButton from 'components/v2/IconButton'
import { AddCircleIcon, CircleCloseIcon } from 'components/Icons'
import { InputTime } from 'components/InputTime'
import { WarningNotification } from 'components/WarningNotification'
import { ILocationAutocomplete, LocationAutocomplete } from 'features/RoutingProjects/components/LocationAutocomplete'
import { IRoutingProjectPlanModel } from 'features/RoutingProjects/types'
import {
  CHANGE_ROUTING_PROJECT_PLAN_CONSTRAINTS,
  CREATE_ROUTING_PROJECT_PLAN_OPTIMIZATION
} from 'features/RoutingProjects/mutations'
import { ICreateConstraintsFormValues } from 'features/RoutingProjects/types'
import { GET_ROUTING_PROJECT_PLAN } from 'features/RoutingProjects/queries'
import { RoutingProjectPlan } from 'gql/generated/graphql'
import { kilometersToMeters, metersToKilometers } from 'utils/number'
import { schema } from './schema'
import EmptyState from 'components/v2/EmptyState'
import { isRoutingProjectOptimizationInfeasibleError } from 'utils/error'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.div`
  color: #000;
  font-size: 16px;
  font-weight: 500;
`

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`

const AddVehicleIconButton = styled(IconButton)`
  padding: 5px 11px;
`

const DeletableVehicleRow = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr min-content;
`

const DeleteVehicleIconButton = styled.div`
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  position: relative;
  top: 30px;
`

const StyledCloseIcon = styled(CircleCloseIcon)`
  fill: #b0b0b0 !important;
  width: 20px;
  height: 20px;
`

const StyledButton = styled(Button)`
  background-color: #00c875;
  border-color: #00c875;

  &:hover {
    background-color: #00c875;
  }

  &:active {
    background-color: #00c875;
  }

  &:disabled {
    background-color: #f0f0f0;
    border: 1px solid #f0f0f0;
  }
`

const messages = defineMessages({
  stopsLimitPlaceholder: {
    id: 'routingProjects.create.constraints.stopsLimit.placeholder',
    defaultMessage: 'Enter limit'
  },
  waitingTimePlaceholder: {
    id: 'routingProjects.create.constraints.waitingTime.placeholder',
    defaultMessage: 'E.g.: 10 min'
  },
  originPlaceholder: {
    id: 'routingProjects.create.constraints.origin.placeholder',
    defaultMessage: 'Select origin'
  },
  vehicleNumberPlaceholder: {
    id: 'routingProjects.create.constraints.vehicle.number.placeholder',
    defaultMessage: 'Enter number'
  },
  vehicleCapacityPlaceholder: {
    id: 'routingProjects.create.constraints.vehicle.capacity.placeholder',
    defaultMessage: 'Enter capacity'
  },
  maxDistancePlaceholder: {
    id: 'routingProjects.create.constraints.maxDistance.placeholder',
    defaultMessage: 'Enter distance'
  },
  maxDurationPlaceholder: {
    id: 'routingProjects.create.constraints.maxDuration.placeholder',
    defaultMessage: 'E.g.: 50 min'
  }
})

interface ICreateConstraintsFormProps {
  planModel: IRoutingProjectPlanModel
  routingProjectId: string
  closePanel: () => void
  planLoading?: boolean
}

export const CreateConstraintsForm = ({
  planModel,
  routingProjectId,
  closePanel,
  planLoading
}: ICreateConstraintsFormProps): ReactElement => {
  const intl = useIntl()
  const client = useApolloClient()
  const [isSubmitting, setSubmitting] = useState(false)
  const newConstraintsRef = useRef<ICreateConstraintsFormValues | null>(null)

  const [getRoutingProjectPlan] = useLazyQuery(GET_ROUTING_PROJECT_PLAN, {
    fetchPolicy: 'network-only'
  })
  const [changeRoutingProjectPlanConstraints, { error: saveConstraintsError }] = useMutation(
    CHANGE_ROUTING_PROJECT_PLAN_CONSTRAINTS
  )
  const [optimizePlan, { error: optimizationError }] = useMutation(CREATE_ROUTING_PROJECT_PLAN_OPTIMIZATION)
  const initialValues: ICreateConstraintsFormValues = {
    origin: planModel?.constraints?.origin || null,
    destination: planModel?.constraints?.destination || null,
    vehicles: !isEmpty(planModel?.constraints?.vehicles)
      ? (planModel?.constraints?.vehicles as ICreateConstraintsFormValues['vehicles'])
      : [{ number: null, capacity: null }],
    maxStops: planModel?.constraints?.maxStops || null,
    maxDuration: planModel?.constraints?.maxDuration || null,
    maxDistance: planModel?.constraints?.maxDistance ? metersToKilometers(planModel?.constraints.maxDistance) : null,
    stopDuration: planModel?.constraints?.stopDuration || null,
    startTime: planModel?.constraints?.startTime || null,
    endTime: planModel?.constraints?.endTime || null
  }

  const fetchCurrentPlan = async (): Promise<void> => {
    client.refetchQueries({ include: [GET_ROUTING_PROJECT_PLAN] })
    const response = await getRoutingProjectPlan({
      variables: {
        input: {
          id: routingProjectId,
          planID: planModel.id
        }
      }
    })
    setSubmitting(false)
    if (!isEmpty((response.data?.routingProjectPlan.plan as RoutingProjectPlan).optimization?.routes)) {
      closePanel()
    }
  }

  const handleSubmit = async (values: ICreateConstraintsFormValues): Promise<void> => {
    setSubmitting(true)
    newConstraintsRef.current = {
      ...values,
      maxDistance: values.maxDistance ? kilometersToMeters(values.maxDistance) : null
    }
    try {
      await changeRoutingProjectPlanConstraints({
        variables: {
          input: {
            id: routingProjectId,
            plan: {
              id: planModel.id,
              constraints: {
                originID: values.origin?.id || undefined,
                destinationID: values.destination?.id || undefined,
                vehicles: values.vehicles.map((vehicle) => ({
                  number: vehicle.number as number,
                  capacity: vehicle.capacity as number
                })),
                maxStops: values.maxStops || undefined,
                maxDuration: values.maxDuration || undefined,
                maxDistance: values.maxDistance ? kilometersToMeters(values.maxDistance) : undefined,
                stopDuration: values.stopDuration || undefined,
                startTime: values.startTime || undefined,
                endTime: values.endTime || undefined
              }
            }
          }
        }
      })
      await optimizePlan({
        variables: {
          input: {
            id: routingProjectId,
            plan: {
              id: planModel.id
            }
          }
        }
      })
      setTimeout(() => {
        fetchCurrentPlan()
      }, 3000)
    } catch (error) {
      setSubmitting(false)
    }
  }

  const noRoutesCreated = isEmpty(planModel?.optimization.routes) && !isEmpty(planModel?.optimization.skippedLocations)

  if (planLoading) {
    return (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  }

  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={handleSubmit} enableReinitialize>
      {({ handleSubmit, values, touched, errors, handleBlur, setFieldValue, setValues, dirty }): ReactElement => {
        return (
          <form onSubmit={handleSubmit}>
            <Container>
              <Title>
                <FormattedMessage id="routingProjects.create.constraints.title" defaultMessage="Constraints" />
              </Title>
              <div>
                <Label>
                  <FormattedMessage id="routingProjects.create.constraints.dataset" defaultMessage="Dataset" />
                </Label>
                <Input type="text" name="dataset" value={planModel.dataset?.name} disabled />
              </div>
              <Title>
                <FormattedMessage id="routingProjects.create.constraints.stops.title" defaultMessage="Stops" />
              </Title>
              <TwoColumns>
                <div>
                  <Label>
                    <FormattedMessage id="routingProjects.create.constraints.stopsLimit" defaultMessage="Stops Limit" />
                  </Label>
                  <InputNumber
                    id="maxStops"
                    name="maxStops"
                    value={!isNull(values.maxStops) ? values.maxStops : ''}
                    placeholder={intl.formatMessage(messages.stopsLimitPlaceholder)}
                    onChange={(value: string): void => {
                      setFieldValue('maxStops', value !== '' ? parseInt(value) : null)
                    }}
                    onBlur={handleBlur}
                    touched={touched.maxStops}
                    errors={errors.maxStops}
                  />
                </div>
                <div>
                  <Label>
                    <FormattedMessage
                      id="routingProjects.create.constraints.stopWaitingTime"
                      defaultMessage="Waiting Time"
                    />
                  </Label>
                  <InputNumber
                    id="stopDuration"
                    name="stopDuration"
                    placeholder={intl.formatMessage(messages.waitingTimePlaceholder)}
                    value={!isNull(values.stopDuration) ? values.stopDuration : ''}
                    onChange={(value: string): void => {
                      setFieldValue('stopDuration', value !== '' ? parseInt(value) : null)
                    }}
                    onBlur={handleBlur}
                    touched={touched.stopDuration}
                    errors={errors.stopDuration}
                    maskOptions={{
                      prefix: '',
                      suffix: ' min'
                    }}
                  />
                </div>
              </TwoColumns>
              <div>
                <Label>
                  <FormattedMessage id="routingProjects.create.constraints.origin" defaultMessage="Origin" />
                </Label>
                <LocationAutocomplete
                  id="origin"
                  name="origin"
                  placeholder={intl.formatMessage(messages.originPlaceholder)}
                  onChange={(location: ILocationAutocomplete): void => {
                    setFieldValue('origin', location?.id ? location : null)
                  }}
                  locations={
                    planModel.dataset?.locations?.map((location) => {
                      return {
                        id: location.id,
                        label: location.label
                      }
                    }) || []
                  }
                  value={values.origin?.label || ''}
                  touched={touched.origin}
                  errors={errors.origin}
                />
              </div>
              <div>
                <Label>
                  <FormattedMessage id="routingProjects.create.constraints.destination" defaultMessage="Destination" />
                </Label>
                <LocationAutocomplete
                  id="destination"
                  name="destination"
                  placeholder={intl.formatMessage(messages.originPlaceholder)}
                  onChange={(location: ILocationAutocomplete): void => {
                    setFieldValue('destination', location?.id ? location : null)
                  }}
                  locations={
                    planModel.dataset?.locations?.map((location) => {
                      return {
                        id: location.id,
                        label: location.label
                      }
                    }) || []
                  }
                  value={values.destination?.label || ''}
                  touched={touched.destination}
                  errors={errors.destination}
                />
              </div>
              <FieldArray
                name="vehicles"
                render={(arrayHelpers): ReactElement => (
                  <>
                    <TitleWrapper>
                      <Title>
                        <FormattedMessage id="routingProjects.create.constraints.fleet.title" defaultMessage="Fleet" />
                      </Title>
                      <AddVehicleIconButton
                        icon={<AddCircleIcon />}
                        onClick={(): void => {
                          arrayHelpers.push({ number: null, capacity: null })
                        }}
                      />
                    </TitleWrapper>
                    {values.vehicles.map((vehicle, index) => {
                      const isDeletable = values.vehicles.length > 1
                      const vehicleForm = (
                        <TwoColumns>
                          <div>
                            <Label>
                              <FormattedMessage
                                id="routingProjects.create.constraints.vehicle.number"
                                defaultMessage="Number of vehicles"
                              />
                            </Label>
                            <InputNumber
                              id={`vehicles.${index}.number`}
                              name={`vehicles.${index}.number`}
                              placeholder={intl.formatMessage(messages.vehicleNumberPlaceholder)}
                              value={!isNull(vehicle.number) ? vehicle.number : ''}
                              onChange={(value: string): void => {
                                arrayHelpers.replace(index, {
                                  ...vehicle,
                                  number: value !== '' ? parseInt(value) : null
                                })
                              }}
                              touched={getIn(touched, `vehicles.${index}.number`)}
                              errors={getIn(errors, `vehicles.${index}.number`)}
                            />
                          </div>
                          <div>
                            <Label>
                              <FormattedMessage
                                id="routingProjects.create.constraints.vehicle.capacity"
                                defaultMessage="Capacity"
                              />
                            </Label>
                            <InputNumber
                              id={`vehicles.${index}.capacity`}
                              name={`vehicles.${index}.capacity`}
                              placeholder={intl.formatMessage(messages.vehicleCapacityPlaceholder)}
                              value={!isNull(vehicle.capacity) ? vehicle.capacity : ''}
                              onChange={(value: string): void => {
                                arrayHelpers.replace(index, {
                                  ...vehicle,
                                  capacity: value !== '' ? parseInt(value) : null
                                })
                              }}
                              touched={getIn(touched, `vehicles.${index}.capacity`)}
                              errors={getIn(errors, `vehicles.${index}.capacity`)}
                            />
                          </div>
                        </TwoColumns>
                      )
                      return (
                        <Fragment key={index}>
                          {isDeletable ? (
                            <DeletableVehicleRow>
                              {vehicleForm}
                              <div>
                                <DeleteVehicleIconButton onClick={(): void => arrayHelpers.remove(index)}>
                                  <StyledCloseIcon />
                                </DeleteVehicleIconButton>
                              </div>
                            </DeletableVehicleRow>
                          ) : (
                            vehicleForm
                          )}
                        </Fragment>
                      )
                    })}
                  </>
                )}
              />
              <Title>
                <FormattedMessage id="routingProjects.create.constraints.distance.title" defaultMessage="Distance" />
              </Title>
              <div>
                <Label>
                  <FormattedMessage id="routingProjects.create.constraints.maxDistance" defaultMessage="Distance Max" />
                </Label>
                <InputNumber
                  id="maxDistance"
                  name="maxDistance"
                  placeholder={intl.formatMessage(messages.maxDistancePlaceholder)}
                  value={!isNull(values.maxDistance) ? values.maxDistance : ''}
                  onChange={(value: string): void => {
                    setFieldValue('maxDistance', value !== '' ? parseFloat(value) : null)
                  }}
                  onBlur={handleBlur}
                  touched={touched.maxDistance}
                  errors={errors.maxDistance}
                  maskOptions={{
                    prefix: '',
                    suffix: ' km',
                    allowDecimal: true
                  }}
                />
              </div>
              <Title>
                <FormattedMessage id="routingProjects.create.constraints.duration.title" defaultMessage="Duration" />
              </Title>
              <div>
                <Label>
                  <FormattedMessage id="routingProjects.create.constraints.maxDuration" defaultMessage="Duration Max" />
                </Label>
                <InputNumber
                  id="maxDuration"
                  name="maxDuration"
                  placeholder={intl.formatMessage(messages.maxDurationPlaceholder)}
                  value={!isNull(values.maxDuration) ? values.maxDuration : ''}
                  onChange={(value: string): void => {
                    setFieldValue('maxDuration', value !== '' ? parseInt(value) : null)
                  }}
                  onBlur={handleBlur}
                  touched={touched.maxDuration}
                  errors={errors.maxDuration}
                  maskOptions={{
                    prefix: '',
                    suffix: ' min'
                  }}
                />
              </div>
              <TwoColumns>
                <div>
                  <Label>
                    <FormattedMessage id="routingProjects.create.constraints.startAt" defaultMessage="Start Hour" />
                  </Label>
                  <InputTime
                    id="startTime"
                    name="startTime"
                    value={values.startTime || ''}
                    onChange={(value: string): void => {
                      setValues({
                        ...values,
                        startTime: value
                      })
                    }}
                    onBlur={handleBlur}
                    touched={touched.startTime}
                    errors={errors.startTime}
                    disabled={!isEmpty(values.endTime)}
                  />
                </div>
                <div>
                  <Label>
                    <FormattedMessage id="routingProjects.create.constraints.endAt" defaultMessage="End Hour" />
                  </Label>
                  <InputTime
                    id="endTime"
                    name="endTime"
                    value={values.endTime || ''}
                    onChange={(value: string): void => {
                      setValues({
                        ...values,
                        endTime: value
                      })
                    }}
                    onBlur={handleBlur}
                    touched={touched.endTime}
                    errors={errors.endTime}
                    disabled={!isEmpty(values.startTime)}
                  />
                </div>
              </TwoColumns>
              {saveConstraintsError && (
                <ErrorMessage>
                  <FormattedMessage
                    id="routingProjects.create.constraints.error"
                    defaultMessage="There was an error while saving constraints"
                  />
                </ErrorMessage>
              )}
              {optimizationError && (
                <ErrorMessage>
                  {isRoutingProjectOptimizationInfeasibleError(optimizationError) ? (
                    <FormattedMessage
                      id="routingProjects.create.constraints.optimizationInfeasible"
                      defaultMessage="The optimization is infeasible. Please check your constraints."
                    />
                  ) : (
                    <FormattedMessage
                      id="routingProjects.create.constraints.optimizationError"
                      defaultMessage="There was an error while optimizing the plan"
                    />
                  )}
                </ErrorMessage>
              )}
              {noRoutesCreated && !isSubmitting && !dirty && (
                <WarningNotification>
                  <FormattedMessage
                    id="routingProjects.create.constraints.noRoutesCreated"
                    defaultMessage="No route could be generated. Please check your constraints."
                  />
                </WarningNotification>
              )}
              <StyledButton kind="primary" loading={isSubmitting}>
                <FormattedMessage id="routingProjects.create.constraints.submit" defaultMessage="Optimize" />
              </StyledButton>
            </Container>
          </form>
        )
      }}
    </Formik>
  )
}
